import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Storage } from '@ionic/storage-angular';
import { environment } from '../../environments/environment';
import { UserService } from 'src/app/services/user.service';
import { BehaviorSubject } from 'rxjs';
import { PaymentService } from './payment.service';

export interface SubscriptionHistoryEntry {
  id?: string;
  status?: string | null;
  planId?: string | null;
  firstBillingDate?: string | null;
  nextBillingDate?: string | null;
  paidThroughDate?: string | null;
  expirationDate?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface SubscriptionTrialInfo {
  accountType?: string | null;
  subscriptionStartDate?: string | null;
  subscriptionEndDate?: string | null;
  trialEndDate?: string | null;
  ownerPremiumTrialStart?: string | null;
  ownerPremiumTrialEnd?: string | null;
}

export interface SubscriptionHistoryEvent {
  action: string;
  date: string;
  at?: string;
  metadata?: Record<string, unknown> | null;
}

export interface SubscriptionHistoryResponse {
  subscriptions: SubscriptionHistoryEntry[];
  trial: SubscriptionTrialInfo;
  events: SubscriptionHistoryEvent[];
}

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
    public paymentNonce = new BehaviorSubject<boolean>(false);
    private userApiUrl = environment.claimApi;

    constructor(
        public http: HttpClient,
        private userService: UserService,
        private paymentService: PaymentService
    ) {}

    async create(account_id, subscriptionData) {
        var optionsUrl = `${this.userApiUrl}/account/${account_id}/subscription`;
        const body: Record<string, unknown> = {
            type: 'kilnlink',
            planId: subscriptionData.frequency,
            billingCycles: (subscriptionData.renewals || 1),
            numberControllers: subscriptionData.numberControllers,
        };
        const nonce = this.paymentService.getNonce();
        if (nonce) {
            body.nonce = nonce;
        }
        return this.http.post(optionsUrl, body, this.headersWithAuthToken(false)).toPromise();
    }

    async update(account_id, subscription_id, subscriptionData) {
        var optionsUrl = `${this.userApiUrl}/account/${account_id}/subscription/${subscription_id}`;
        const body: Record<string, unknown> = {
            type: 'kilnlink',
            planId: subscriptionData.frequency,
            billingCycles: (subscriptionData.renewals || 1),
            numberControllers: subscriptionData.numberControllers,
        };
        const nonce = this.paymentService.getNonce();
        if (nonce) {
            body.nonce = nonce;
        }
        return this.http.put(optionsUrl, body, this.headersWithAuthToken(false)).toPromise();
    }

    async delete(account_id, subscription_id) {
        var optionsUrl = `${this.userApiUrl}/account/${account_id}/subscription/${subscription_id}`;
        return this.http.delete(optionsUrl, this.headersWithAuthToken(false)).toPromise()
    }

    async get(account_id) {
        var optionsUrl = `${this.userApiUrl}/account/${account_id}/subscription`;
        return this.http.get(optionsUrl, this.headersWithAuthToken(false)).toPromise();   
    }

    async getHistory(account_id: number): Promise<SubscriptionHistoryResponse> {
        const optionsUrl = `${this.userApiUrl}/account/${account_id}/subscription/history`;
        const result = await this.http
            .get<SubscriptionHistoryResponse>(optionsUrl, this.headersWithAuthToken(false))
            .toPromise();
        return result as SubscriptionHistoryResponse;
    }

    private headersWithAuthToken(includeAppNameToken = true) {
        const loginData = this.userService.getLoginData();

        if(loginData) {
            var headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'x-access-token': loginData.token
            };

            if(includeAppNameToken) { headers['x-app-name-token'] = 'kiln-link'; }
            const httpOptions = {
            headers: new HttpHeaders(headers)
            }

            return httpOptions;
        } 
    }
}